import React, { useEffect, useMemo, useState } from 'react';
import { List } from 'immutable';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import PropTypes from 'prop-types';
import { TogglePanel, TogglePanelCollection } from '@els/els-react--toggle-panel';
import UserDatabases from '@Services/UserDatabases';

import BarChartFilter from '@Containers/ResultsFilters/ResultsFiltersComponents/BarChartFilter';
import CheckboxFilter from '@Containers/ResultsFilters/ResultsFiltersComponents/CheckboxFilter';
import StructureFilter from '@Containers/ResultsFilters/ResultsFiltersComponents/StructureFilter';
import FullTextFilter from '@Containers/ResultsFilters/ResultsFiltersComponents/FullTextFilter';
import CommercialSubstancesPreferences from '@Containers/ResultsFilters/ResultsFiltersComponents/CommercialSubstancesPreferences/CommercialSubstancesPreferences';
import {
  commercialSubstancesFilterOrder,
  preferenceFilterIds,
  shippingTimeFilterId,
} from '@Containers/ResultsFilters/ResultsFiltersConstants';

import './ListOfFilters.scss';

const spinnerId = 'ListOfFilters-spinner';
const filtersMap = {
  1: BarChartFilter,
  2: BarChartFilter,
  4: CheckboxFilter,
  5: StructureFilter,
  6: FullTextFilter,
};

function normalizeFilterOrderValue(value = '') {
  return `${value}`.trim().toLowerCase();
}

// A commercialSubstancesFilterOrder entry matches a filter by its backend id (on `value`) or its display
// `title`, because some commercial filters are identified by id and others only carry a label.
function getCommercialSubstancesFilterOrderIndex(filter) {
  const filterValue = normalizeFilterOrderValue(filter.get('value'));
  const filterTitle = normalizeFilterOrderValue(filter.get('title'));

  return commercialSubstancesFilterOrder.findIndex((orderValue) => {
    const normalizedOrderValue = normalizeFilterOrderValue(orderValue);
    return filterValue === normalizedOrderValue || filterTitle === normalizedOrderValue;
  });
}

// Listed filters sort by their design position; unlisted filters keep their original order, after the listed block.
function getCommercialSubstancesSortIndex(filter, originalIndex) {
  const orderIndex = getCommercialSubstancesFilterOrderIndex(filter);
  return orderIndex === -1 ? commercialSubstancesFilterOrder.length + originalIndex : orderIndex;
}

// Hides the internal preference filters, and on RCS databases reorders the remaining filters to match the
// commercial-substances design order.
function getVisibleOrderedFilters(filters, databaseId) {
  const visibleFilters = filters?.filter(
    (filter) => !preferenceFilterIds.includes(filter.get('id')) && !preferenceFilterIds.includes(filter.get('value'))
  );

  if (!visibleFilters || !UserDatabases.isRCSDatabase(databaseId)) {
    return visibleFilters;
  }

  return visibleFilters
    .map((filter, index) => ({ filter, index }))
    .sort(
      (left, right) =>
        getCommercialSubstancesSortIndex(left.filter, left.index) - getCommercialSubstancesSortIndex(right.filter, right.index)
    )
    .map(({ filter }) => filter);
}

function ListOfFilters({ toggleFilter, filters, keyifyParamsForFiltersRequest, paramsForFiltersRequest, refreshing = false }) {
  const [expandedPanels, setExpandedPanels] = useState([]);
  const { context, databaseId } = paramsForFiltersRequest;
  const visibleFilters = useMemo(() => getVisibleOrderedFilters(filters, databaseId), [databaseId, filters]);

  useEffect(() => {
    if (visibleFilters) {
      const panels = visibleFilters
        .filter((f) => f.get('expanded') === true)
        ?.map((f) => f.get('id'))
        .toArray();
      setExpandedPanels(panels);
      Logger.info('Setting expanded panels:', panels);
    } else {
      setExpandedPanels([]);
    }
  }, [visibleFilters]);

  if (!filters) {
    return <RxSpinnerComponent autoStart spinnerId={spinnerId} />;
  }
  const filtersForTogglePanels = visibleFilters.filter((f) => [1, 2, 5].includes(f.get('type')));
  const filtersNoTogglePanelsTop = visibleFilters.filter((f) => f.get('type') === 6);
  const filtersNoTogglePanelsBottom = visibleFilters.filter((f) => f.get('type') === 4);

  if (visibleFilters.size !== filtersForTogglePanels.size + filtersNoTogglePanelsBottom.size + filtersNoTogglePanelsTop.size) {
    const filteredOutIds = [
      ...filtersForTogglePanels.map((f) => f.get('id')),
      ...filtersNoTogglePanelsBottom.map((f) => f.get('id')),
      ...filtersNoTogglePanelsTop.map((f) => f.get('id')),
    ];
    const filtersDiff = filters.filter((f) => !filteredOutIds.includes(f.get('id')));
    Logger.warn('Some filters have unsupported types and will not be rendered: ', filtersDiff.toJS());
  }
  // Render non-toggle full-text filters above the toggle panels for prominence, and keep checkbox filters below to preserve the existing filter grouping.
  const renderNonToggleFilter = (filter, extraProps = {}) => {
    const id = filter.get('id');
    const type = filter.get('type');
    const FilterComponent = filtersMap[type];
    return (
      <FilterComponent
        key={id}
        data-e2e={`filter-${id?.replace(/[.\s]/g, '-')}`}
        keyifyParamsForFiltersRequest={keyifyParamsForFiltersRequest}
        context={context}
        filter={filter}
        toggleFilter={toggleFilter}
        disableControls={id === shippingTimeFilterId || filter.get('value') === shippingTimeFilterId}
        {...extraProps}
      />
    );
  };

  return (
    <div className="list-of-filters" aria-busy={refreshing || undefined} inert={refreshing ? '' : undefined}>
      <CommercialSubstancesPreferences />
      {filtersNoTogglePanelsTop.map((filter) => renderNonToggleFilter(filter, { className: 'u-els-margin-top-2x' }))}
      {filtersForTogglePanels && filtersForTogglePanels.size > 0 && (
        <TogglePanelCollection
          data-e2e="list-of-filters-toggle-panels-collection"
          className="list-of-filters-collection"
          headerLevel="3"
          thinKeyline
          noTopKeyline
          initialOpenPanels={expandedPanels}>
          {filtersForTogglePanels
            ?.map((filter) => {
              const id = filter.get('id');
              const type = filter.get('type');
              const FilterComponent = filtersMap[type];

              return (
                <TogglePanel
                  id={id}
                  key={id}
                  data-e2e={`filter-panel-${id?.replace(/[.\s]/g, '-')}`}
                  className="filter-panel"
                  header={filter.get('title') || 'Filter'}
                  onClick={() => {
                    toggleFilter(filter);
                  }}>
                  <FilterComponent
                    data-e2e={`filter-${id?.replace(/[.\s]/g, '-')}`}
                    keyifyParamsForFiltersRequest={keyifyParamsForFiltersRequest}
                    context={context}
                    filter={filter}
                    toggleFilter={toggleFilter}
                    disableControls={id === shippingTimeFilterId || filter.get('value') === shippingTimeFilterId}
                  />
                </TogglePanel>
              );
            })
            .toArray()}
        </TogglePanelCollection>
      )}
      {filtersNoTogglePanelsBottom.map((filter) => renderNonToggleFilter(filter, { className: 'u-els-margin-top-2x' }))}
    </div>
  );
}

ListOfFilters.propTypes = {
  filters: PropTypes.instanceOf(List),
  toggleFilter: PropTypes.func.isRequired,
  keyifyParamsForFiltersRequest: PropTypes.string.isRequired,
  paramsForFiltersRequest: PropTypes.object.isRequired,
  refreshing: PropTypes.bool,
};

export default ListOfFilters;
